import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import { FaSearch, FaShoppingCart, FaFilter, FaTimes, FaStar, FaChevronDown } from 'react-icons/fa';
import { partsStoreService } from '../services/partsStoreService';
import { useCart } from '../contexts/CartContext';

const PartsStore = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [addedProductId, setAddedProductId] = useState(null);
  const { addItem, itemCount } = useCart();

  useEffect(() => {
    loadProducts();
  }, [selectedCategory, sortBy, page]);

  useEffect(() => {
    loadCategories();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
      loadProducts();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const loadProducts = async () => {
    setLoading(true);
    const result = await partsStoreService.getProducts({
      category: selectedCategory,
      search: searchQuery,
      sort: sortBy,
      page,
      limit: 24
    });
    setProducts(result.data);
    setTotalPages(result.totalPages);
    setLoading(false);
  };

  const loadCategories = async () => {
    const { data } = await partsStoreService.getCategories();
    setCategories(data);
  };

  const handleAddToCart = (product) => {
    addItem(product);
    setAddedProductId(product.id);
    setTimeout(() => setAddedProductId(null), 1500);
  };

  const formatPrice = (p) => `$${parseFloat(p).toFixed(2)}`;

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Performance Parts and Accessories - OBD Scanners, Tools</title>
        <meta name="description" content="Shop performance parts, OBD scanners, engine components and tools at Paradise-Performance. Ships direct or pick up in Paradise, TX. Quality parts for European and domestic vehicles." />
        <link rel="canonical" href="https://paradise-performance.com/parts" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://paradise-performance.com/parts" />
        <meta property="og:title" content="Auto Parts Store - Paradise-Performance" />
        <meta property="og:description" content="OBD scanners, engine components, tools and more. Pick up in Paradise TX or get it shipped." />
      </Helmet>
      {/* Hero */}
      <div className="relative bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 25% 25%, rgba(59,130,246,0.3) 0%, transparent 50%), radial-gradient(circle at 75% 75%, rgba(99,102,241,0.2) 0%, transparent 50%)'
          }} />
        </div>
        <div className="max-w-7xl mx-auto px-4 py-16 md:py-24 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Performance Parts & Accessories
            </h1>
            <p className="text-lg text-blue-200 max-w-2xl mx-auto mb-8">
              OBD scanners, engine components, tools and everything your build needs. Ships direct or pick up in Paradise, TX.
            </p>
            <div className="max-w-xl mx-auto relative">
              <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search parts, tools, accessories..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-4 rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 text-white placeholder-gray-300 focus:ring-2 focus:ring-blue-400 focus:border-transparent focus:bg-white/15 transition-all"
              />
            </div>
          </motion.div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Toolbar */}
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setMobileFiltersOpen(!mobileFiltersOpen)}
              className="md:hidden flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium"
            >
              <FaFilter className="text-gray-500" /> Filters
            </button>
            <div className="hidden md:flex flex-wrap gap-2">
              <button
                onClick={() => { setSelectedCategory('all'); setPage(1); }}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  selectedCategory === 'all'
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'bg-white text-gray-700 border border-gray-200 hover:border-blue-300'
                }`}
              >
                All Products
              </button>
              {categories.map(cat => (
                <button
                  key={cat}
                  onClick={() => { setSelectedCategory(cat); setPage(1); }}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    selectedCategory === cat
                      ? 'bg-blue-600 text-white shadow-md'
                      : 'bg-white text-gray-700 border border-gray-200 hover:border-blue-300'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Link to="/cart" className="relative flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium hover:border-blue-300 transition-colors">
              <FaShoppingCart />
              Cart
              {itemCount > 0 && (
                <span className="absolute -top-2 -right-2 w-5 h-5 bg-blue-600 text-white text-xs rounded-full flex items-center justify-center font-bold">
                  {itemCount}
                </span>
              )}
            </Link>
            <select
              value={sortBy}
              onChange={(e) => { setSortBy(e.target.value); setPage(1); }}
              className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
            >
              <option value="newest">Newest First</option>
              <option value="price_low">Price: Low to High</option>
              <option value="price_high">Price: High to Low</option>
              <option value="name_az">Name: A-Z</option>
            </select>
          </div>
        </div>

        {/* Mobile Filters */}
        <AnimatePresence>
          {mobileFiltersOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="md:hidden overflow-hidden mb-6"
            >
              <div className="bg-white rounded-xl p-4 border border-gray-200 space-y-2">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-semibold text-sm">Categories</span>
                  <button onClick={() => setMobileFiltersOpen(false)}><FaTimes className="text-gray-400" /></button>
                </div>
                <button
                  onClick={() => { setSelectedCategory('all'); setPage(1); setMobileFiltersOpen(false); }}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm ${selectedCategory === 'all' ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'}`}
                >
                  All Products
                </button>
                {categories.map(cat => (
                  <button
                    key={cat}
                    onClick={() => { setSelectedCategory(cat); setPage(1); setMobileFiltersOpen(false); }}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm ${selectedCategory === cat ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'}`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Product Grid */}
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="bg-white rounded-xl border border-gray-200 animate-pulse">
                <div className="aspect-square bg-gray-200 rounded-t-xl" />
                <div className="p-4 space-y-3">
                  <div className="h-4 bg-gray-200 rounded w-3/4" />
                  <div className="h-3 bg-gray-200 rounded w-1/2" />
                  <div className="h-5 bg-gray-200 rounded w-1/3" />
                </div>
              </div>
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-24">
            <div className="text-6xl mb-4">🔧</div>
            <h2 className="text-2xl font-bold text-gray-700 mb-2">No products found</h2>
            <p className="text-gray-500">Try adjusting your search or filters.</p>
          </div>
        ) : (
          <motion.div
            initial="hidden"
            animate="visible"
            variants={{
              hidden: {},
              visible: { transition: { staggerChildren: 0.04 } }
            }}
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6"
          >
            {products.map((product) => (
              <motion.div
                key={product.id}
                variants={{
                  hidden: { opacity: 0, y: 20 },
                  visible: { opacity: 1, y: 0 }
                }}
                className="bg-white rounded-xl border border-gray-200 overflow-hidden group hover:shadow-lg hover:border-blue-200 transition-all duration-300"
              >
                <Link to={`/parts/${product.slug}`} className="block">
                  <div className="aspect-square bg-gray-100 overflow-hidden relative">
                    {product.images?.[0] ? (
                      <img
                        src={product.images[0]}
                        alt={product.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-300 text-5xl">
                        🔧
                      </div>
                    )}
                    {product.featured && (
                      <span className="absolute top-2 left-2 bg-amber-500 text-white text-xs px-2 py-1 rounded-full font-medium flex items-center gap-1">
                        <FaStar className="text-[10px]" /> Featured
                      </span>
                    )}
                    {product.sale_price && (
                      <span className="absolute top-2 right-2 bg-red-500 text-white text-xs px-2 py-1 rounded-full font-bold">
                        SALE
                      </span>
                    )}
                    {product.stock_quantity <= 0 && (
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                        <span className="bg-white text-gray-900 text-sm font-bold px-4 py-2 rounded-full">Out of Stock</span>
                      </div>
                    )}
                  </div>
                </Link>

                <div className="p-4">
                  <p className="text-xs text-blue-600 font-medium mb-1">{product.category}</p>
                  <Link to={`/parts/${product.slug}`}>
                    <h3 className="font-semibold text-gray-900 text-sm leading-snug mb-2 group-hover:text-blue-600 transition-colors line-clamp-2">
                      {product.name}
                    </h3>
                  </Link>

                  <div className="flex items-center justify-between mt-3">
                    <div>
                      {product.sale_price ? (
                        <div className="flex items-center gap-2">
                          <span className="text-lg font-bold text-green-600">{formatPrice(product.sale_price)}</span>
                          <span className="text-sm text-gray-400 line-through">{formatPrice(product.price)}</span>
                        </div>
                      ) : (
                        <span className="text-lg font-bold text-gray-900">{formatPrice(product.price)}</span>
                      )}
                    </div>
                    {product.stock_quantity > 0 && (
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          handleAddToCart(product);
                        }}
                        className={`p-2.5 rounded-full transition-all duration-300 ${
                          addedProductId === product.id
                            ? 'bg-green-500 text-white scale-110'
                            : 'bg-blue-600 text-white hover:bg-blue-700 hover:scale-105'
                        }`}
                        title="Add to cart"
                      >
                        {addedProductId === product.id ? '✓' : <FaShoppingCart className="text-sm" />}
                      </button>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-2 mt-12">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
              <button
                key={p}
                onClick={() => { setPage(p); window.scrollTo(0, 0); }}
                className={`w-10 h-10 rounded-lg flex items-center justify-center text-sm font-medium transition-all ${
                  page === p
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'bg-white text-gray-700 border border-gray-200 hover:border-blue-300'
                }`}
              >
                {p}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PartsStore;
